import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, CalendarX, History, Plus, type LucideIcon } from "lucide-react";
import { Paginacao } from "@/shared/components/Paginacao";
import { useAuth } from "@/core/hooks/useAuth";
import { CardEscala } from "@/modules/escala/components/CardEscala";
import { abaDaEscala, type AbaEscala } from "@/modules/escala/format";
import { useEscalas, useMinisteriosParaCriarEscala } from "@/modules/escala/hooks/useEscalas";
import type { EscalaComRelacoes } from "@/modules/escala/services/escalaService";

const ITENS_POR_PAGINA = 10;

// Rótulos exibidos na tela. As chaves internas (agendadas/realizadas) não mudam.
const ABAS: { chave: AbaEscala; rotulo: string }[] = [
  { chave: "agendadas", rotulo: "Próximas" },
  { chave: "realizadas", rotulo: "Anteriores" },
  { chave: "canceladas", rotulo: "Canceladas" },
];

const VAZIO: Record<AbaEscala, { icone: LucideIcon; titulo: string; texto: string }> = {
  agendadas: {
    icone: CalendarCheck,
    titulo: "Nenhuma escala próxima",
    texto: "Quando houver escalas marcadas, elas aparecem aqui.",
  },
  realizadas: {
    icone: History,
    titulo: "Nenhuma escala anterior",
    texto: "As escalas que já passaram ficam aqui.",
  },
  canceladas: {
    icone: CalendarX,
    titulo: "Nenhuma escala cancelada",
    texto: "As canceladas ficam aqui, separadas das outras.",
  },
};

function ordenarPorData(lista: EscalaComRelacoes[], crescente: boolean): EscalaComRelacoes[] {
  const direcao = crescente ? 1 : -1;
  return [...lista].sort((a, b) => direcao * (new Date(a.data_hora).getTime() - new Date(b.data_hora).getTime()));
}

/**
 * Listagem de escalas visíveis para a pessoa logada. As abas
 * "Próximas"/"Anteriores"/"Canceladas" e o filtro por ministério são
 * recortes CLIENT-SIDE sobre o mesmo conjunto já autorizado pela RLS — nunca
 * uma segunda consulta nem um filtro que decida visibilidade (isso é papel do
 * banco). Escala cancelada fica sempre em "Canceladas", mesmo com data futura.
 */
export function ListaEscalasPage() {
  const navigate = useNavigate();
  const { pessoa } = useAuth();
  const { data: escalas, isLoading, isError } = useEscalas(pessoa?.id);
  // O botão "Nova escala" é só conveniência de tela: aparece quando a pessoa
  // lidera/gerencia ao menos um ministério ativo (a mesma lista que a página
  // de criação usa, em cache). Quem decide se pode criar continua sendo o
  // banco (`escala_criar`). Enquanto carrega ou se falhar, fica escondido.
  const { data: ministeriosParaCriar } = useMinisteriosParaCriarEscala(pessoa?.id);
  const podeCriarEscala = (ministeriosParaCriar?.length ?? 0) > 0;

  const [aba, setAba] = useState<AbaEscala>("agendadas");
  const [filtroMinisterio, setFiltroMinisterio] = useState("");
  const [pagina, setPagina] = useState(1);

  const nomesDeMinisterio = new Map<string, string>();
  for (const e of escalas ?? []) nomesDeMinisterio.set(e.ministerio_id, e.ministerioNome);
  const ministeriosNaLista = [...nomesDeMinisterio.entries()]
    .map(([id, nome]) => ({ id, nome }))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

  const agora = new Date();
  const doMinisterio = (escalas ?? []).filter((e) => !filtroMinisterio || e.ministerio_id === filtroMinisterio);
  const grupos: Record<AbaEscala, EscalaComRelacoes[]> = {
    agendadas: ordenarPorData(doMinisterio.filter((e) => abaDaEscala(e, agora) === "agendadas"), true),
    realizadas: ordenarPorData(doMinisterio.filter((e) => abaDaEscala(e, agora) === "realizadas"), false),
    canceladas: ordenarPorData(doMinisterio.filter((e) => abaDaEscala(e, agora) === "canceladas"), false),
  };
  const daAba = grupos[aba];

  const totalPaginas = Math.max(1, Math.ceil(daAba.length / ITENS_POR_PAGINA));
  const inicio = (pagina - 1) * ITENS_POR_PAGINA;
  const paginaAtual = daAba.slice(inicio, inicio + ITENS_POR_PAGINA);
  const vazio = VAZIO[aba];
  const IconeVazio = vazio.icone;

  function mudarAba(nova: AbaEscala) {
    setAba(nova);
    setPagina(1);
  }

  return (
    <main className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-8 sm:px-6 sm:py-10">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-bold text-text">Escala</h1>
        {podeCriarEscala && (
          <button
            type="button"
            onClick={() => navigate("/escalas/nova")}
            className="flex min-h-10 items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-on-primary transition-colors hover:bg-primary-hover"
          >
            <Plus size={16} aria-hidden /> Nova escala
          </button>
        )}
      </div>

      <div role="tablist" aria-label="Escalas" className="grid grid-cols-3 gap-1 rounded-2xl bg-surface-2 p-1">
        {ABAS.map((a) => {
          const selecionada = aba === a.chave;
          return (
            <button
              key={a.chave}
              type="button"
              role="tab"
              aria-selected={selecionada}
              onClick={() => mudarAba(a.chave)}
              className={`flex min-h-11 items-center justify-center gap-1 rounded-xl px-1 text-[13px] font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-hover focus-visible:ring-offset-2 focus-visible:ring-offset-surface-2 ${
                selecionada ? "bg-primary text-on-primary shadow-sm" : "text-muted hover:text-text"
              }`}
            >
              {a.rotulo}
              <span
                className={`min-w-5 rounded-full px-1.5 py-px text-center text-[11px] font-extrabold ${
                  selecionada ? "bg-on-primary text-primary" : "bg-border text-muted"
                }`}
              >
                {grupos[a.chave].length}
              </span>
            </button>
          );
        })}
      </div>

      {ministeriosNaLista.length > 1 && (
        <select
          value={filtroMinisterio}
          onChange={(e) => {
            setFiltroMinisterio(e.target.value);
            setPagina(1);
          }}
          aria-label="Filtrar por ministério"
          className="w-full rounded-md border border-transparent bg-surface-2 px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary sm:w-64"
        >
          <option value="">Todos os ministérios</option>
          {ministeriosNaLista.map((m) => (
            <option key={m.id} value={m.id}>
              {m.nome}
            </option>
          ))}
        </select>
      )}

      {isLoading && <p className="py-8 text-center text-sm text-muted">Carregando escalas...</p>}

      {isError && (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          Não foi possível carregar as escalas. Tente novamente em instantes.
        </p>
      )}

      {!isLoading && !isError && daAba.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border px-5 py-10 text-center">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-surface-2 text-muted">
            <IconeVazio size={24} aria-hidden />
          </span>
          <p className="text-[15px] font-bold text-text">{vazio.titulo}</p>
          <p className="text-sm text-muted">{vazio.texto}</p>
        </div>
      )}

      {!isLoading && !isError && paginaAtual.length > 0 && (
        <div className="flex flex-col gap-2.5">
          {paginaAtual.map((e) => (
            <CardEscala key={e.id} escala={e} aba={aba} onAbrir={() => navigate(`/escalas/${e.id}`)} />
          ))}
          <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={setPagina} />
        </div>
      )}
    </main>
  );
}
